import React, { useState } from "react";
import {
    ShieldCheck,
    Settings,
    Users,
    PlusCircle,
    Sliders,
    BarChart3,
    PlayCircle,
} from "lucide-react";
import { AdminTrainingVideoModal } from "../components/AdminTrainingVideoModal";
import { highlightBrand } from "../utils/brandUtils";
import GuideLayout, { type GuideSection } from "../components/pages/GuideLayout";

const AdminGuidePage: React.FC = () => {
    const [showVideoModal, setShowVideoModal] = useState(false);
    const sections: GuideSection[] = [
        {
            id: "intro",
            title: "Introduction",
            icon: <ShieldCheck className="w-5 h-5 text-indigo-600" />,
            content: (
                <div className="space-y-4">
                    <p>
                        As an Administrator, you have oversight over your entire organisation's sustainability journey on the {highlightBrand("LAB ZER0")} platform. Your role involves managing members, configuring constants, and monitoring lab progress.
                    </p>
                </div>
            ),
        },
        {
            id: "org-setup",
            title: "Organisation Setup",
            icon: <Settings className="w-5 h-5 text-slate-600" />,
            content: (
                <div className="space-y-4">
                    <p>Managing your organisation's foundational settings:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Org Details:</strong> View and manage your organisation's name and subscription status.</li>
                        <li><strong>Lab Limits:</strong> Your subscription determines the total number of labs you can register. If you reach this limit, you'll need to contact support to upgrade your plan.</li>
                    </ul>
                </div>
            ),
        },
        {
            id: "invitations",
            title: "Managing Invitations",
            icon: <Users className="w-5 h-5 text-blue-600" />,
            content: (
                <div className="space-y-4">
                    <p>Expand your team by inviting new members to the platform.</p>
                    <div className="p-4 rounded-xl border border-indigo-100 bg-indigo-50/50">
                        <h4 className="font-bold text-indigo-900 mb-2">Two Types of Invites:</h4>
                        <div className="space-y-3">
                            <div className="flex gap-3">
                                <div className="text-indigo-600 font-bold">•</div>
                                <p><strong>Invite Admins:</strong> Grants full administrative access. Requires confirmation of a security warning before sending.</p>
                            </div>
                            <div className="flex gap-3">
                                <div className="text-indigo-600 font-bold">•</div>
                                <p><strong>Invite Users:</strong> Standard access for lab members to perform assessments and checklists.</p>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            id: "variables",
            title: "Variable Management",
            icon: <Sliders className="w-5 h-5 text-amber-600" />,
            content: (
                <div className="space-y-4">
                    <p>Configure the mathematical constants that drive the Impact Reports across all your labs.</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Emission Factors:</strong> Set conversion rates for energy (kWh to CO2e), water usage, and waste recycling.</li>
                        <li><strong>Consistency:</strong> Variables apply to all labs in your organisation, ensuring fair and consistent reporting.</li>
                    </ul>
                </div>
            ),
        },
        {
            id: "lab-oversight",
            title: "Lab Oversight",
            icon: <PlusCircle className="w-5 h-5 text-emerald-600" />,
            content: (
                <div className="space-y-4">
                    <p>Maintain the structure of your labs and their certification progress.</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Register Labs:</strong> Add new labs with their specific type (Wet or Dry) and Champion user.</li>
                        <li><strong>Auditor Assignment:</strong> When a lab is ready for certification, assign a member from another lab to perform the peer audit.</li>
                        <li><strong>View All:</strong> Monitor all labs in one central dashboard.</li>
                    </ul>
                </div>
            ),
        },
        {
            id: "reporting",
            title: "Organisation Reporting",
            icon: <BarChart3 className="w-5 h-5 text-rose-600" />,
            content: (
                <div className="space-y-4">
                    <p>Analyse aggregated data to see how your entire institute is becoming more sustainable.</p>
                    <p>
                        The <strong>Global Impact Report</strong> allows you to filter and compare performance across different lab types and departments, helping you identify areas that need more support or celebrate your biggest sustainability wins.
                    </p>
                </div>
            ),
        },
        {
            id: "training-video",
            title: "Admin Training Video",
            icon: <PlayCircle className="w-5 h-5 text-indigo-600" />,
            content: (
                <div className="space-y-4">
                    <p>
                        Watch our comprehensive platform induction video tailored specifically for Organisation Administrators. This guide covers everything from setting up your organisation to managing complex lab certifications.
                    </p>
                    <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white rounded-lg shadow-sm">
                                <PlayCircle className="text-indigo-600" />
                            </div>
                            <div>
                                <h4 className="font-bold text-indigo-900">Platform Induction</h4>
                                <p className="text-xs text-indigo-700">Duration: 12 minutes</p>
                            </div>
                        </div>
                        <button
                            className="btn btn-primary btn-sm gap-2"
                            onClick={() => setShowVideoModal(true)}
                        >
                            <PlayCircle size={16} />
                            Watch Training Video
                        </button>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <>
            <GuideLayout
                pageTitle="Admin Guide"
                backPath="/dashboard"
                backLabel="Back to Admin Dashboard"
                sidebarTitle="Admin Navigator"
                sections={sections}
                theme="indigo"
                cardVariant="primary"
            />
            <AdminTrainingVideoModal
                isOpen={showVideoModal}
                onClose={() => setShowVideoModal(false)}
            />
        </>
    );
};

AdminGuidePage.displayName = "AdminGuidePage";

export default AdminGuidePage;

